import {
  AnalyticsConfiguration,
  AnalyticsConfigurationKeys,
  AnalyticsConfigurations,
  ANALYTICS_NAMESPACE,
  defaultBestPerformingDashboard,
} from '@epicuro-next/constants/rule-engine/analytics-rules';
import { UserRole } from '@epicuro-next/data/users';
import {
  getValueFromConfigState,
  selectConfigurationsState,
  selectConfigurationValueFrom,
} from '@epicuro-next/platform/configuration';
import { isNullish, memoize } from '@epicuro-next/utilities';
import { createSelector } from '@ngrx/store';
import { BestPerformingDashboardSettings } from 'libs/constants/rule-engine/analytics-rules/src/lib/models/best-performing-dashboard-settings';

import { AnalyticsEntity, State } from '../interfaces';
import { entityToListViewers } from '../utils/list-viewers';
import { entityToLiveViewers } from '../utils/live-viewers';

import { selectAnalyticsState } from './analytics-base.selectors';

export const selectListAnalyticsEntityFactory = memoize((id: string) =>
  createSelector(selectAnalyticsState, (state: State) => {
    return state.entities[id];
  }),
);

export const selectListAnalyticsEntity = createSelector(
  selectAnalyticsState,
  (state: State, id: string) => {
    return state.entities[id];
  },
);

export const selectListAnalyticsReport = createSelector(
  selectListAnalyticsEntity,
  (entity) => entity?.report,
);

export const selectCanSeeAnalytics = selectConfigurationValueFrom<
  AnalyticsConfigurationKeys.EnableListAnalytics,
  AnalyticsConfigurations
>(
  ANALYTICS_NAMESPACE,
  AnalyticsConfigurationKeys.EnableListAnalytics,
  AnalyticsConfiguration[AnalyticsConfigurationKeys.EnableListAnalytics],
);

export const selectCanSeeLiveViewers = createSelector(
  selectConfigurationsState,
  selectCanSeeAnalytics,
  (config, canSeeAnalytics) => {
    if (!config || isNullish(canSeeAnalytics)) return undefined;
    if (!canSeeAnalytics) return false;

    return getValueFromConfigState<
      AnalyticsConfigurationKeys.EnableListAnalyticsLiveViewers,
      AnalyticsConfigurations
    >(
      config,
      ANALYTICS_NAMESPACE,
      AnalyticsConfigurationKeys.EnableListAnalyticsLiveViewers,
      false,
    );
  },
);

export const selectCanSeeExtendedAnalytics = createSelector(
  selectConfigurationsState,
  (config) => {
    if (!config) return undefined;

    return getValueFromConfigState<
      AnalyticsConfigurationKeys.EnableExtendedAnalytics,
      AnalyticsConfigurations
    >(
      config,
      ANALYTICS_NAMESPACE,
      AnalyticsConfigurationKeys.EnableExtendedAnalytics,
      false,
    );
  },
);

export const selectListExchange = createSelector(
  selectAnalyticsState,
  (state) =>
    state.exchange ?? {
      currency: '',
      date: new Date().toISOString(),
      originalCurrency: '',
      rate: 1,
    },
);

export const selectCanSeeAnalyticsRoles = createSelector(
  selectConfigurationsState,
  (config) => {
    return getValueFromConfigState<
      AnalyticsConfigurationKeys.EnableListAnalyticsByRoles,
      AnalyticsConfigurations
    >(
      config,
      ANALYTICS_NAMESPACE,
      AnalyticsConfigurationKeys.EnableListAnalyticsByRoles,
      [UserRole.Administrator],
    ) as UserRole[];
  },
);

export const selectBestPerformingDashboard = createSelector(
  selectConfigurationValueFrom<
    AnalyticsConfigurationKeys.BestPerformingDashboard,
    AnalyticsConfigurations
  >(
    ANALYTICS_NAMESPACE,
    AnalyticsConfigurationKeys.BestPerformingDashboard,
    AnalyticsConfiguration[AnalyticsConfigurationKeys.BestPerformingDashboard],
  ),
  (configurationValue) => {
    try {
      if (!configurationValue) {
        return defaultBestPerformingDashboard;
      }
      return JSON.parse(configurationValue) as BestPerformingDashboardSettings;
    } catch {
      console.warn(
        'Invalid settings for best performing dashboard found, falling back to default settings.',
      );
      return defaultBestPerformingDashboard;
    }
  },
);

export const selectLiveViewingUsers = createSelector(
  selectListAnalyticsEntity,
  (entity) => {
    if (!entity) return [];
    return entityToLiveViewers(entity);
  },
);

export const selectAnalyticsEntityTopSellingProducts = (entityId: string) =>
  createSelector(
    selectAnalyticsState,
    (state) => state.entities[entityId]?.topSellingProducts,
  );

export const selectAnalyticsEntityTopListViewers = (entityId: string) =>
  createSelector(selectAnalyticsState, (state) =>
    entityToListViewers(state.entities[entityId] as AnalyticsEntity),
  );
